import { useMemo, useState, useCallback } from "react";
import { getFinancialSummary, generateInvoicePlan } from "@/data/invoiceEngine";
import { getCustomerPaymentSummary } from "@/data/financialClearance";
import { useParams, useSearchParams } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { usePortal } from "@/data/portal/context";
import {
  computeWhatMattersNow,
  computeSmartTimeline,
  computeTrustInfo,
  computeProjectReadiness,
  createPortalEvent,
  type PortalEvent,
  type PortalAction,
} from "@/data/portal/intelligence";
import { BrandedPortalLayout } from "@/components/portal/BrandedPortalLayout";
import { ProjectReadinessPanel } from "@/components/portal/ProjectReadinessPanel";
import { WhatMattersNowCard } from "@/components/portal/WhatMattersNowCard";
import { PortalActionLayer } from "@/components/portal/PortalActionLayer";
import { SmartTimeline } from "@/components/portal/SmartTimeline";
import { TrustLayer } from "@/components/portal/TrustLayer";
import { PortalCommunicationThread } from "@/components/portal/PortalCommunicationThread";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  AlertCircle, MessageSquare, Camera, AlertTriangle,
  Clock, Shield, Send, DollarSign, CheckCircle, CreditCard,
} from "lucide-react";
import type { Job } from "@/data/types";
import { formatAddress } from "@/lib/addressUtils";

export default function WhiteLabelPortal() {
  const { jobId } = useParams<{ jobId?: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { jobs, issues, setIssues } = useAppData();
  const { branding, hasFeature } = usePortal();

  const [activeActionId, setActiveActionId] = useState<string | null>(null);
  const [portalEvents, setPortalEvents] = useState<PortalEvent[]>([]);

  const job = useMemo(() => {
    if (jobId) return jobs.find(j => j.id === jobId);
    if (token) return jobs.find(j => j.id === token);
    return jobs[0];
  }, [jobs, jobId, token]);

  const openIssues = useMemo(
    () => (job ? issues.filter(i => i.jobId === job.id && i.status !== "Resolved" && i.status !== "Closed") : []),
    [issues, job]
  );

  const whatMatters = useMemo(
    () => (job ? computeWhatMattersNow(job, openIssues, job.changeOrders || []) : null),
    [job, openIssues]
  );

  const smartTimeline = useMemo(
    () => (job ? computeSmartTimeline(job, openIssues) : []),
    [job, openIssues]
  );

  const trustInfo = useMemo(
    () => (job ? computeTrustInfo(job, openIssues) : null),
    [job, openIssues]
  );

  const readiness = useMemo(
    () => (job ? computeProjectReadiness(job) : null),
    [job]
  );

  const handleActionClick = useCallback((actionId: string) => {
    setActiveActionId(actionId);
  }, []);

  const handleActionComplete = useCallback((event: PortalEvent) => {
    setPortalEvents(prev => [...prev, event]);

    // Create structured issue if needed
    if (event.type === "issue_submitted" && job) {
      const newIssue = {
        id: `ISS-portal-${Date.now()}`,
        jobId: job.id,
        issueType: "Customer Complaint" as const,
        severity: "Medium" as const,
        description: event.description,
        photo: "",
        owner: "",
        status: "Open" as const,
        dateCreated: event.timestamp,
        source: "Customer" as const,
      };
      setIssues(prev => [...prev, newIssue]);
    }
  }, [job, setIssues]);

  // Quick actions for the bottom of the page
  const quickActions: PortalAction[] = useMemo(() => [
    {
      id: "quick-issue", type: "submit_issue" as const, label: "Report an Issue",
      description: "Let us know about a problem", urgency: "info" as const,
    },
    {
      id: "quick-photos", type: "upload_photos" as const, label: "Share Photos",
      description: "Upload project photos", urgency: "info" as const,
    },
  ], []);

  if (!job) {
    return (
      <BrandedPortalLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <AlertCircle className="h-12 w-12 text-muted-foreground/30 mb-4" />
          <h2 className="text-lg font-semibold text-foreground">Project Not Found</h2>
          <p className="text-sm text-muted-foreground mt-1">Please check your link or contact support.</p>
        </div>
      </BrandedPortalLayout>
    );
  }

  return (
    <BrandedPortalLayout>
      <div className="space-y-5">
        {/* ── What Matters Now ── */}
        {whatMatters && (
          <WhatMattersNowCard data={whatMatters} onActionClick={handleActionClick} />
        )}

        {/* ── Project Readiness ── */}
        {readiness && <ProjectReadinessPanel readiness={readiness} />}

        {/* ── Active Action ── */}
        {activeActionId && (
          <PortalActionLayer
            jobId={job.id}
            actions={[...(whatMatters?.confirmationsRequired || []), ...quickActions]}
            activeActionId={activeActionId}
            onActionComplete={handleActionComplete}
            onClose={() => setActiveActionId(null)}
          />
        )}

        {/* ── Smart Timeline ── */}
        {hasFeature("timeline_enabled") && smartTimeline.length > 0 && (
          <Card className="border-border/40">
            <CardHeader className="pb-3">
              <CardTitle className="text-xs font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> Project Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SmartTimeline steps={smartTimeline} />
            </CardContent>
          </Card>
        )}

        {/* ── Trust Layer ── */}
        {trustInfo && (
          <Card className="border-border/40">
            <CardHeader className="pb-3">
              <CardTitle className="text-xs font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5" /> Your Project
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TrustLayer trust={trustInfo} />
            </CardContent>
          </Card>
        )}

        {/* ── Communication Thread ── */}
        {hasFeature("messaging_enabled") && (
          <Card className="border-border/40">
            <CardHeader className="pb-3">
              <CardTitle className="text-xs font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-1.5">
                <MessageSquare className="h-3.5 w-3.5" /> Messages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PortalCommunicationThread jobId={job.id} customerName={job.customerName} />
            </CardContent>
          </Card>
        )}

        {/* ── Billing & Payments ── */}
        {(() => {
          const summary = getFinancialSummary(job);
          const invoices = job.financials?.invoices ?? [];
          const plan = job.financials?.invoicePlan ?? generateInvoicePlan(job);
          return (
            <Card className="border-border/40">
              <CardHeader className="pb-3">
                <CardTitle className="text-xs font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-1.5">
                  <DollarSign className="h-3.5 w-3.5" /> Billing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-[10px] text-muted-foreground">Contract</p>
                    <p className="text-sm font-bold">${summary.contractValue.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">Paid</p>
                    <p className="text-sm font-bold text-success">${summary.totalPaid.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">Balance</p>
                    <p className={`text-sm font-bold ${summary.outstandingBalance > 0 ? "text-warning" : "text-success"}`}>
                      ${summary.outstandingBalance.toLocaleString()}
                    </p>
                  </div>
                </div>
                {invoices.length > 0 ? (
                  <div className="space-y-1.5">
                    {invoices.map(inv => (
                      <div key={inv.id} className="flex items-center justify-between py-1.5 border-b border-border/30 last:border-0">
                        <div className="flex items-center gap-2">
                          {inv.status === "paid" ? (
                            <CheckCircle className="h-3 w-3 text-success" />
                          ) : (
                            <Clock className="h-3 w-3 text-warning" />
                          )}
                          <span className="text-xs font-medium capitalize">{inv.type}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold">${inv.amount.toLocaleString()}</span>
                          {inv.status === "sent" && (
                            <Button size="sm" variant="outline" className="text-[10px] h-6 px-2">
                              <CreditCard className="h-3 w-3 mr-1" /> Pay Now
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {(() => {
                      const msgs = getCustomerPaymentSummary(job);
                      return msgs.map((msg, i) => (
                        <div key={i} className="flex items-center gap-2 py-1.5 border-b border-border/30 last:border-0">
                          {msg.includes("✓") ? (
                            <CheckCircle className="h-3 w-3 text-success shrink-0" />
                          ) : msg.includes("declined") ? (
                            <AlertTriangle className="h-3 w-3 text-destructive shrink-0" />
                          ) : (
                            <Clock className="h-3 w-3 text-warning shrink-0" />
                          )}
                          <span className="text-xs text-muted-foreground">{msg}</span>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* ── Quick Actions ── */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setActiveActionId("quick-issue")}
            className="flex items-center gap-2.5 p-3.5 rounded-xl border border-border/40 bg-card hover:border-primary/20 hover:bg-primary/5 transition-all text-left"
          >
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-foreground">Report Issue</p>
              <p className="text-[11px] text-muted-foreground">Let us know</p>
            </div>
          </button>
          <button
            onClick={() => setActiveActionId("quick-photos")}
            className="flex items-center gap-2.5 p-3.5 rounded-xl border border-border/40 bg-card hover:border-primary/20 hover:bg-primary/5 transition-all text-left"
          >
            <Camera className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-foreground">Share Photos</p>
              <p className="text-[11px] text-muted-foreground">Upload images</p>
            </div>
          </button>
        </div>

        {/* ── Contact ── */}
        {branding && (
          <div className="text-center py-4 space-y-1">
            {branding.supportPhone && (
              <p className="text-sm text-muted-foreground">
                Call:{" "}
                <a href={`tel:${branding.supportPhone}`} className="text-primary font-medium hover:underline">
                  {branding.supportPhone}
                </a>
              </p>
            )}
            {branding.supportEmail && (
              <p className="text-sm text-muted-foreground">
                Email:{" "}
                <a href={`mailto:${branding.supportEmail}`} className="text-primary font-medium hover:underline">
                  {branding.supportEmail}
                </a>
              </p>
            )}
          </div>
        )}

        {/* ── Event Audit (hidden, for system tracking) ── */}
        {portalEvents.length > 0 && (
          <div className="text-[10px] text-muted-foreground/40 text-center">
            {portalEvents.length} interaction{portalEvents.length !== 1 ? "s" : ""} logged
          </div>
        )}
      </div>
    </BrandedPortalLayout>
  );
}
